import { useState } from "react";
import { ActionIcon, Group, Menu, Modal, Tooltip, Text, Button } from "@mantine/core";
import { Copy, DotsVertical, Edit, Qrcode, Trash } from "tabler-icons-react";
import { addDoc, collection, deleteDoc, doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { generateAndDownloadSingleQR } from "../../../components/shared/bulk-qr-generator";

interface TableRowActionsProps<T extends { id?: string; companyId?: string }> {
  element: T;
  assetName: string;
  collectionName: string;
  nameKey: keyof T;
  idKey: keyof T;
  setEditedElement: (element: T) => void;
  reloadCallback: () => void;
}

export default function TableRowActions<T extends { id?: string; companyId?: string }>({
  element,
  assetName,
  collectionName,
  nameKey,
  idKey,
  setEditedElement,
  reloadCallback,
}: TableRowActionsProps<T>) {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCloning, setIsCloning] = useState(false);

  const itemName = String(element[nameKey] || "Unknown");
  const itemId = String(element[idKey] || "");

  const handleDelete = async () => {
    if (!itemId) return;
    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, collectionName, itemId));
      reloadCallback();
    } catch (e) {
      console.error("Error deleting document:", e);
      alert("Failed to delete document.");
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handleClone = async () => {
    if (!itemId) return;
    setIsCloning(true);
    try {
      const docRef = doc(db, collectionName, itemId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        delete data.id; // Ensure we don't copy the ID if it's in the payload
        // Modify the name/registration to indicate it's a copy
        if (data[nameKey as string]) {
          data[nameKey as string] = `${data[nameKey as string]} (Copy)`;
        }
        await addDoc(collection(db, collectionName), data);
        reloadCallback();
      }
    } catch (e) {
      console.error("Error cloning document:", e);
      alert("Failed to clone document.");
    } finally {
      setIsCloning(false);
    }
  };

  const handleQRDownload = async () => {
    if (!itemId) return;
    await generateAndDownloadSingleQR(assetName, itemId, itemName);
  };

  return (
    <>
      <Modal
        opened={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title={`Delete ${assetName}`}
        centered
      >
        <Text mb="md">
          Are you sure you want to delete "{itemName}"? This action cannot be undone.
        </Text>
        <Group position="right">
          <Button variant="outline" color="gray" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="filled" color="red" onClick={handleDelete} loading={isDeleting}>Delete</Button>
        </Group>
      </Modal>

      <Group spacing="xs" position="left" noWrap>
        <Tooltip label="Edit" withArrow position="top">
          <ActionIcon size="lg" color="cap-blue" variant="outline" aria-label={`Edit ${itemName || "item"}`} onClick={() => setEditedElement(element)}>
            <Edit size={18} />
          </ActionIcon>
        </Tooltip>
        
        <Tooltip label="Delete" withArrow position="top">
          <ActionIcon size="lg" color="red" variant="outline" aria-label={`Delete ${itemName || "item"}`} onClick={() => setDeleteConfirmOpen(true)}>
            <Trash size={18} />
          </ActionIcon>
        </Tooltip>

        <Menu
          size="md"
          position="bottom"
          placement="end"
          styles={{
            item: {
              minHeight: 44,
              padding: "10px 16px",
              fontSize: 15,
              fontWeight: 500,
            },
            itemIcon: {
              width: 20,
              height: 20,
              marginRight: 12,
            },
          }}
          control={
            <ActionIcon size="lg" variant="outline" color="gray" aria-label={`More actions for ${itemName || "item"}`}>
              <DotsVertical size={18} />
            </ActionIcon>
          }
        >
          <Menu.Item icon={<Copy size={16} />} onClick={handleClone} disabled={isCloning}>
            Clone
          </Menu.Item>
          <Menu.Item icon={<Qrcode size={16} />} onClick={handleQRDownload}>
            Download QR
          </Menu.Item>
        </Menu>
      </Group>
    </>
  );
}
